import { CONFIG, type Config } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";
import axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import type { Logger } from "../../logger";
import { LOGGER } from "../../logger";
import { NoResponseError } from "../errors/no-response.error";
import { ServerError } from "../errors/server.error";
import { type Http, type HttpConfig, HttpMethod } from "../http.contract";
import { HTTP } from "../http.types";

@Injectable(HTTP)
export class AxiosService implements Http {
  private readonly logger: Logger;

  constructor(
    @Inject(CONFIG) private config: Config,
    @Inject(LOGGER) private loggerFactory: Logger
  ) {
    this.logger = this.loggerFactory.create("Http");
  }

  async isPerimeterexError(error: unknown): Promise<boolean> {
    if (!error || typeof error !== "object") {
      return false;
    }

    const axiosError = error as AxiosError;
    const statusCode = axiosError.response?.status;

    if (statusCode !== 403) {
      return false;
    }

    const headers = axiosError.response?.headers || {};
    const data: any = axiosError.response?.data;

    // Verifica headers com indícios de PerimeterX
    const hasPxHeaders = Object.keys(headers).some(
      (key) =>
        key.toLowerCase().startsWith("px-") ||
        key.toLowerCase().includes("perimeterx")
    );

    // Verifica corpo da resposta com chaves típicas do PerimeterX
    const hasPxBody =
      data && typeof data === "object" && ("appId" in data || "uuid" in data);

    return hasPxHeaders || !!hasPxBody;
  }

  async request(url: string, config?: HttpConfig): Promise<string> {
    const axiosConfig: AxiosRequestConfig = {
      method: config?.method ?? HttpMethod.GET,
      url,
      params: config?.params ?? {},
      headers: this.config.disabledHeaders ? {} : config?.headers ?? {},
      // data: config?.body ?? {},
    };

    if (
      config?.body &&
      config?.headers?.["Content-Type"] === "application/json"
    ) {
      axiosConfig.data = JSON.parse(config.body.toString());
    }

    this.logger.info("[request] sending request: ", url);
    this.logger.info("[request] request config: ", config);

    const axiosInstance = axios.create();

    try {
      this.logger.info("[request] axiosConfig: ", axiosConfig);

      const response = await axiosInstance.request(axiosConfig);

      this.logger.info("[request] response status text: ", response.statusText);
      this.logger.info("[request] response status code: ", response.status);
      this.logger.info("[request] response data: ", response.data);

      return response.data;
    } catch (error: any) {
      if (await this.isPerimeterexError(error)) {
        this.logger.error("[request] Perimeterex error detected", error);

        throw error;
      }

      if (error.response) {
        const errorData = error.response.data;

        this.logger.error("[request] request failed", {
          method: axiosConfig.method,
          url,
          params: axiosConfig.params,
          status: error.response.status,
          statusText: error.response.statusText,
          data: errorData,
        });

        throw new ServerError(errorData, {
          status: error.response.status,
          statusText: error.response.statusText,
          url,
          method: axiosConfig.method,
        });
      }

      if (error.request) {
        this.logger.error("[request] no response received", {
          method: axiosConfig.method,
          url,
          code: error.code,
          message: error.message,
        });

        throw new NoResponseError(
          `No response received from ${axiosConfig.method} ${url}${
            error.code ? ` (${error.code})` : ""
          }`
        );
      }

      this.logger.error("[request] something went wrong", url, error);

      throw error;
    }
  }
}
